import { v4 } from 'uuid'
import { config } from '../appConfig'

const CLIENT_ID_KEY = 'clientId'
const SESSION_KEY = 'ga.session'
const FIRST_SEEN_KEY = 'ga.firstSeenDate'
const ATTRIBUTION_KEY = 'ga.attribution'

const SESSION_TIMEOUT_MS = 30 * 60 * 1000

export { ATTRIBUTION_KEY, FIRST_SEEN_KEY }

export interface SessionInfo {
  sessionId: string
  sessionNumber: number
  isNewSession: boolean
}

export interface Attribution {
  campaign_source?: string
  campaign_medium?: string
  campaign_name?: string
  campaign_term?: string
  campaign_content?: string
  gclid?: string
}

interface SessionRecord {
  id: string
  startedAt: number
  lastActivityAt: number
  count: number
}

let tail: Promise<unknown> = Promise.resolve()

const serialize = <T>(task: () => Promise<T>): Promise<T> => {
  const result = tail.then(task, task)
  tail = result.catch(() => undefined)
  return result
}

const readRecord = <T>(value: unknown): T | undefined => {
  if (!value) return undefined
  if (typeof value !== 'string') return value as T
  try {
    return JSON.parse(value) as T
  } catch {
    return undefined
  }
}

const toDateStamp = (timestamp: number) => {
  const date = new Date(timestamp)
  const pad = (value: number) => `${value}`.padStart(2, '0')
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`
}

export const getOrCreateClientId = () =>
  serialize(async () => {
    let clientId = await config.localStorage.get(CLIENT_ID_KEY)
    if (!clientId) {
      clientId = v4()
      await config.localStorage.set(CLIENT_ID_KEY, clientId)
    }
    return clientId as string
  })

export const resolveSession = () =>
  serialize<SessionInfo>(async () => {
    const now = Date.now()
    const stored = readRecord<SessionRecord>(await config.localStorage.get(SESSION_KEY))
    const isNewSession = !stored?.id || now - stored.lastActivityAt > SESSION_TIMEOUT_MS

    const record: SessionRecord = isNewSession
      ? { id: v4(), startedAt: now, lastActivityAt: now, count: (stored?.count ?? 0) + 1 }
      : { ...(stored as SessionRecord), lastActivityAt: now }

    await config.localStorage.set(SESSION_KEY, JSON.stringify(record))

    return { sessionId: record.id, sessionNumber: record.count, isNewSession }
  })

export const getFirstSeenDate = () =>
  serialize(async () => {
    const stored = await config.localStorage.get(FIRST_SEEN_KEY)
    if (stored) return stored as string

    const firstSeenDate = toDateStamp(Date.now())
    await config.localStorage.set(FIRST_SEEN_KEY, firstSeenDate)
    return firstSeenDate
  })

const ATTRIBUTION_KEYS: (keyof Attribution)[] = [
  'campaign_source',
  'campaign_medium',
  'campaign_name',
  'campaign_term',
  'campaign_content',
  'gclid',
]

export const getAttribution = async (): Promise<Attribution> => {
  try {
    const stored = readRecord<Attribution>(await config.localStorage.get(ATTRIBUTION_KEY)) || {}

    return ATTRIBUTION_KEYS.reduce<Attribution>((acc, key) => {
      if (stored[key]) acc[key] = stored[key]
      return acc
    }, {})
  } catch {
    return {}
  }
}
